import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PuzzlePieceIcon, ArrowDownTrayIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useAdminPlugins, useCreatePlugin, useUpdatePlugin, useDeletePlugin, useBridgePluginInfo } from '@/hooks/useAdmin';
import { downloadBridgePlugin } from '@/services/admin.api';
import { showError } from '@/utils/toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';

export function AdminPluginsPage() {
  const { t } = useTranslation();
  const { data: plugins, isLoading, isError, refetch } = useAdminPlugins();
  const { data: bridgeInfo } = useBridgePluginInfo();
  const createPlugin = useCreatePlugin();
  const updatePlugin = useUpdatePlugin();
  const deletePlugin = useDeletePlugin();

  const [createOpen, setCreateOpen] = useState(false);
  const [editPlugin, setEditPlugin] = useState<{ id: string; name: string; slug: string; description?: string; price?: string; status: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', price: '', status: 'active' });
  const [copied, setCopied] = useState(false);

  const resetForm = () => setForm({ name: '', slug: '', description: '', price: '', status: 'active' });

  const handleCreate = async () => {
    await createPlugin.mutateAsync(form);
    setCreateOpen(false);
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editPlugin) return;
    await updatePlugin.mutateAsync({ pluginId: editPlugin.id, data: form });
    setEditPlugin(null);
    resetForm();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deletePlugin.mutateAsync(deleteId);
    setDeleteId(null);
  };

  const openEdit = (plugin: { id: string; name: string; slug: string; description?: string; price?: string; status: string }) => {
    setForm({ name: plugin.name, slug: plugin.slug, description: plugin.description ?? '', price: plugin.price ?? '', status: plugin.status });
    setEditPlugin(plugin);
  };

  const handleDownloadPlugin = async () => {
    try {
      const blob = await downloadBridgePlugin();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'wooconnect-bridge.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(t('common.error'));
    }
  };

  const copyInstallSteps = () => {
    navigator.clipboard.writeText(t('plugins.installSteps'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.plugins')}</h1>
        <Button onClick={() => { resetForm(); setCreateOpen(true); }}>{t('admin.addPlugin')}</Button>
      </div>

      {/* Bridge Plugin Download Card */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 dark:bg-brand-900/20">
            <PuzzlePieceIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('plugins.bridgeTitle')}</h3>
              {bridgeInfo?.version && (
                <Badge label={`v${bridgeInfo.version}`} variant="info" />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{t('plugins.bridgeDescription')}</p>

            {bridgeInfo && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                {bridgeInfo.requiresWP && <span>{t('plugins.bridgeRequiresWP', { version: bridgeInfo.requiresWP })}</span>}
                {bridgeInfo.requiresWP && bridgeInfo.requiresPHP && <span>|</span>}
                {bridgeInfo.requiresPHP && <span>{t('plugins.bridgeRequiresPHP', { version: bridgeInfo.requiresPHP })}</span>}
                {bridgeInfo.requiresPHP && bridgeInfo.requiresWC && <span>|</span>}
                {bridgeInfo.requiresWC && <span>{t('plugins.bridgeRequiresWC', { min: bridgeInfo.requiresWC, max: bridgeInfo.testedWC })}</span>}
              </div>
            )}

            {bridgeInfo?.changelog?.[0] && (
              <div className="mt-3 rounded-lg bg-brand-50 p-3 dark:bg-brand-900/20">
                <p className="mb-1.5 text-xs font-medium uppercase text-brand-600 dark:text-brand-400">{t('plugins.bridgeWhatsNew')} - v{bridgeInfo.changelog[0].version}</p>
                <ul className="space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                  {bridgeInfo.changelog[0].changes.map((change, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-500" />
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
              <p className="mb-1 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('plugins.installGuide')}</p>
              <ol className="list-inside list-decimal space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <li>{t('plugins.step1')}</li>
                <li>{t('plugins.step2')}</li>
                <li>{t('plugins.step3')}</li>
                <li>{t('plugins.step4')}</li>
              </ol>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={handleDownloadPlugin}>
                <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                {t('plugins.downloadBridge')} {bridgeInfo?.version ? `v${bridgeInfo.version}` : ''}
              </Button>
              <Button variant="outline" size="sm" onClick={copyInstallSteps}>
                <ClipboardDocumentIcon className="mr-1.5 h-4 w-4" />
                {copied ? t('plugins.copied') : t('plugins.copySteps')}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}</div>
      ) : plugins?.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plugins.map((plugin: { id: string; name: string; slug: string; description?: string; price?: string; status: string }) => (
            <Card key={plugin.id}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{plugin.name}</h3>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{plugin.slug}</p>
                </div>
                <Badge label={plugin.status} variant={plugin.status === 'active' ? 'success' : 'default'} />
              </div>
              {plugin.description && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{plugin.description}</p>}
              {plugin.price && <p className="mt-2 text-lg font-bold text-gray-900 dark:text-gray-100">{plugin.price}</p>}
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(plugin)}>{t('common.edit')}</Button>
                <Button variant="danger" size="sm" onClick={() => setDeleteId(plugin.id)}>{t('common.delete')}</Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState icon={PuzzlePieceIcon} title={t('admin.noPlugins')} actionLabel={t('admin.addPlugin')} onAction={() => { resetForm(); setCreateOpen(true); }} />
      )}

      {/* Create Modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title={t('admin.addPlugin')} size="sm">
        <div className="space-y-4">
          <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label={t('common.slug')} value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
          <Input label={t('products.description')} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <Input label={t('common.price')} value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} loading={createPlugin.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editPlugin} onClose={() => setEditPlugin(null)} title={t('common.edit')} size="sm">
        <div className="space-y-4">
          <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label={t('common.slug')} value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
          <Input label={t('products.description')} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <Input label={t('products.regularPrice')} value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
          <Select label={t('common.status')} options={[{ value: 'active', label: t('common.active') }, { value: 'inactive', label: t('common.inactive') }]} value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setEditPlugin(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleUpdate} loading={updatePlugin.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} message={t('common.confirmDelete')} confirmLabel={t('common.delete')} confirmVariant="danger" loading={deletePlugin.isPending} />
    </div>
  );
}
